// Central, non-localised site configuration: URLs, brand constants, asset paths,
// social + press links and the image map. Translatable copy lives in /messages.

export const site = {
  name: "Trein-vertraging.nl",
  legalName: "Trein-vertraging B.V.",
  kvk: "94274177",
  vat: "NL866705375B01",
  address: "Herengracht 420, 1017 BZ Amsterdam",
  // Signs the influencer collaboration agreement, and receives the signed copies.
  signatory: {
    name: "Daan Ykema",
    role: "Directeur",
    email: "daan@trein-vertraging.nl",
  },
  domain: "https://trein-vertraging.nl",
  // Hostnames this app is served under. One spelling, because three separate
  // modules gate on them (PostHog ingestion, the first-touch cookie, and the
  // A/B arm label) and a typo in any one of them fails silently.
  //
  // `paidVariant` is the TRE-931 Google Ads experiment landing page: the same
  // build as the apex, on its own host, deployed with SITE_ENV=ab_variant.
  hosts: {
    apex: "trein-vertraging.nl",
    www: "www.trein-vertraging.nl",
    paidVariant: "start.trein-vertraging.nl",
  },
  appUrl: "https://app.trein-vertraging.nl",
  // Where every CTA on the site goes, and the app's single front door. It is a
  // UNIFIED gateway rather than a login form: Continue with Google / Apple /
  // e-mail, and the app decides for itself whether the address is new. There is
  // no GET /register at all (POST only) and the app root 302s here, so new and
  // returning users arrive at the same screen. The page still titles itself
  // "Inloggen", so do not describe it as signup-only. Verified live 2026-08-16.
  //
  // That ambiguity has a measured cost worth knowing about: `clicked_on_sign_up`
  // runs 2.0-3.35x `sign_up`, because a returning user clicking a `cta_` button
  // to log in mints a sign-up conversion just like a new one does. Nothing here
  // fixes that, and it is not what this constant is for - it is noted so the
  // next person does not read the conversion as pure signup intent.
  //
  // Do NOT route a CTA somewhere else to satisfy a tracking rule. That was
  // tried in PR #115 - a `signupUrl` pointing at the app root, to dodge web GTM
  // trigger 8's condition excluding Click URLs containing `/login` - and it was
  // reverted: it cost users a redirect hop and rescued exactly one CTA. The
  // conversion is made to fire in the markup instead; see CtaButton.
  loginUrl: "https://app.trein-vertraging.nl/login",
  reviewsUrl: "https://www.google.com/search?q=Trein-vertraging.nl",
  // Destination of the /tp short link printed on payout bank statements. This is
  // Trustpilot's "evaluate" URL, which opens the review form directly rather than
  // the profile page, so a user who typed the link by hand lands on the one screen
  // the link asked them for.
  trustpilotReviewUrl: "https://nl.trustpilot.com/evaluate/trein-vertraging.nl",
  email: "info@trein-vertraging.nl",
  rating: { value: 4.8, count: 850, scale: 5 },
  helped: "178.000",
  // Public client-side analytics IDs (safe to ship in the bundle).
  analytics: {
    gtmId: "GTM-58HHSPPW",
    posthogKey: "phc_CMDYt3UEmkCgR4arThPPNYUrPx4xeDyR8Q6ai4rcH2QP",
    posthogHost: "https://eu.i.posthog.com",
    posthogProjectId: "163081", // EU project; used for /admin deep links + status API
    posthogAppHost: "https://eu.posthog.com", // app/REST host (not the i. ingestion host)
    facebookDomainVerification: "gp370bx9o842zhk8bt8u516r1y15ac",
  },
} as const;

export const assets = {
  logo: "/assets/logo-dark.svg", // navy wordmark for light backgrounds (header)
  logoFooter: "/assets/MoZ1zYi4H525GhIr1jnwCp3DOY.svg", // white wordmark for dark footer
  logoMark: "/assets/logo-dark.svg",
  ogImage: "/assets/hUFN4P3SuW61klHfNPo9S1yO4bI.png",
  // Real product imagery
  heroHand: "/assets/SlhwVPSrdgTq6g4gfCY9H9io.png", // hand holding phone with app (unresized PNG source)
  // The hero LCP image: the same hand-holding-phone shot the old Framer page led
  // with, pre-encoded to WebP at two widths. Pre-encoding matters -- handing the
  // optimizer Lambda the 408KB PNG makes the first request per size variant
  // cold-encode for seconds, right on the LCP path.
  //
  // Both keep an alpha channel, compressed lossily (cwebp -alpha_q 70). The
  // source is a phone on transparency with a soft purple glow, and encoding that
  // glow losslessly costs 166KB of alpha -- more than the photo itself.
  // Flattening onto the ink background instead was tried and reverted: it shows
  // as a visible rectangle, because lossy WebP does not hold a perfectly flat
  // colour and the browser colour-manages images but not CSS colours, so the two
  // never match exactly. Lossy alpha gets most of the saving with no seam.
  heroApp: "/assets/hero-app.webp", // 1200w, desktop
  heroAppMobile: "/assets/hero-app-mobile.webp", // 700w, mobile
  heroPhoto: "/assets/hero-platform.png", // two travellers on an NS platform (hero lifestyle)
  heroBlob: "/assets/hero-bg-v2.webp", // photo with soft faded edges baked in (blends into dark card)
  heroMobile: "/assets/hero-mobile.webp", // mobile-optimised hero photo, fills the card width from the bottom
  appOverview: "/assets/iphone-claim-overview.webp", // claim overview €94
  payments: "/assets/iphone-payments.webp", // NS payouts received
  lifestyle: "/assets/7Rx6Gl4feOkr4qBPvXwseQNg3us.webp", // person on a train
  statsHand: "/assets/stats-app-hand.webp", // hand holding phone (claim overview), pre-masked hexagon cutout
  trainPhoto: "/assets/Qf9eUvAkgJCwt88XLHpkM7mwAi0.png", // NS train at sunset
  founders: "/assets/founders.webp", // the two founders in an NS train doorway (about us)
  team: "/assets/team.webp", // group photo of the team (about us)
  teamMichael: "/assets/team-michael.webp", // co-founder portrait (also in the About team list)
  teamDaan: "/assets/team-daan.webp", // co-founder portrait (also in the About team list)
  blob: "/assets/9DUFWebbEuZLs2OQEiWtFHRHziM.png", // decorative accent
  iso27001: "/assets/8Wp3UwvJngKfijf9UjaG76TlBKs.png", // white ISO 27001 information security badge
} as const;

// Press logos. SVG where a clean, freely-licensed vector exists (Wikimedia
// Commons); BNR and De Gelderlander have no free SVG, so they stay PNG.
export const pressLogos = [
  { src: "/assets/press/rtl-nieuws.svg", alt: "RTL Nieuws", href: "https://www.rtl.nl/nieuws/artikel/5457469/trein-vertraging-hoe-krijg-ik-geld-terug-ns" },
  { src: "/assets/press/nos.svg", alt: "NOS", href: "https://nos.nl/" },
  { src: "/assets/press/bnnvara.svg", alt: "BNNVARA Kassa", href: "https://npo.nl/start/serie/kassa/seizoen-20_1/kassa_716/afspelen" },
  { src: "/assets/press/ad.svg", alt: "Algemeen Dagblad", href: "https://www.ad.nl/binnenland/geld-terug-na-treinvertraging-fluitje-van-een-cent-vinding-van-studenten-groot-succes~a12518cc/" },
  { src: "/assets/press/telegraaf.svg", alt: "De Telegraaf", href: "https://www.telegraaf.nl/" },
  { src: "/assets/press/linda.svg", alt: "LINDA.", href: "https://www.linda.nl/" },
] as const;

export const socials = {
  instagram: "https://instagram.com/treinvertraging",
  tiktok: "https://www.tiktok.com/@treinvertraging",
  linkedin: "https://www.linkedin.com/company/trein-vertraging-nl/",
} as const;

export const trustSite = "https://trein-vertraging-bv.eu.trust.site/";

// Internal nav routes (keys map to next-intl pathnames in src/i18n/routing.ts).
export const navRoutes = [
  { key: "home", href: "/" as const },
  { key: "faq", href: "/veelgestelde-vragen" as const },
  { key: "rights", href: "/ken-je-rechten" as const },
  { key: "blog", href: "/blog" as const },
  { key: "vacatures", href: "/vacatures" as const },
  { key: "about", href: "/over-ons" as const },
  { key: "klantenservice", href: "/klantenservice" as const },
] as const;

// The GTM container URL, in one place because two callers must agree on it
// exactly. The locale layout emits `<link rel="preload" as="script">` for this
// string and DeferredGoogleTagManager injects a `<script>` with it; if the two
// ever drift the preload becomes a separate cache entry and gtm.js is fetched
// twice instead of the injection running from cache.
export function gtmScriptUrl(gtmId: string): string {
  return `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`;
}
